import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Trophy, Crown, Medal, Star, Loader2, Globe, Building, Calendar
} from 'lucide-react';

const LeaderboardPage = () => {
  const { childId } = useParams();
  const { api, user } = useAuth();
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [orgLeaderboard, setOrgLeaderboard] = useState([]);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('global');

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [globalRes, weeklyRes, childRes] = await Promise.all([
        api.get('/leaderboard/global?limit=20'),
        api.get('/leaderboard/weekly?limit=20'),
        api.get(`/children/${childId}`)
      ]);
      
      setGlobalLeaderboard(globalRes.data);
      setWeeklyLeaderboard(weeklyRes.data);
      setChild(childRes.data);
      
      // Fetch org leaderboard if user is in an organization
      if (user?.organization_id) {
        try {
          const orgRes = await api.get(`/leaderboard/organization/${user.organization_id}?limit=50`);
          setOrgLeaderboard(orgRes.data);
        } catch (e) {
          console.log('No org leaderboard');
        }
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-slate-500 font-bold">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300';
    if (rank === 2) return 'bg-gradient-to-r from-slate-100 to-slate-200 border-slate-300';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-100 border-amber-300';
    return 'bg-white border-slate-200';
  };

  const getAvatarBg = (index) => {
    const colors = [
      'from-purple-400 to-pink-400',
      'from-blue-400 to-cyan-400',
      'from-green-400 to-emerald-400',
      'from-orange-400 to-red-400',
      'from-indigo-400 to-purple-400',
    ];
    return colors[index % colors.length];
  };

  const LeaderboardList = ({ data, xpKey = 'xp' }) => (
    <div className="space-y-3">
      {data.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No data yet. Start learning to appear on the leaderboard!</p>
        </div>
      ) : (
        data.map((entry, index) => {
          const isCurrentChild = entry.child_id === childId;
          return (
            <motion.div
              key={entry.child_id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                isCurrentChild ? 'ring-2 ring-sky-400 ring-offset-2' : ''
              } ${getRankBg(entry.rank)}`}
              data-testid={`leaderboard-entry-${entry.rank}`}
            >
              <div className="flex-shrink-0">
                {getRankIcon(entry.rank)}
              </div>
              
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarBg(index)} flex items-center justify-center text-white font-bold text-lg`}>
                {entry.name?.charAt(0).toUpperCase()}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800 truncate">
                  {entry.name}
                  {isCurrentChild && (
                    <span className="ml-2 text-xs bg-sky-100 text-sky-600 px-2 py-0.5 rounded-full">You</span>
                  )}
                </p>
                <p className="text-sm text-slate-500">
                  {entry.level && `Level ${entry.level}`}
                </p>
              </div>
              
              <div className="text-right">
                <div className="flex items-center gap-1 text-purple-600 font-bold">
                  <Star className="w-4 h-4 text-yellow-500" />
                  {entry[xpKey]?.toLocaleString() || entry.weekly_xp?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-slate-400">XP</p>
              </div>
            </motion.div>
          );
        })
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white theme-kids">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/challenges/${childId}`}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-yellow-500" />
                <h1 className="font-fredoka text-xl font-bold text-amber-800">Leaderboard</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Child's Current Standing */}
        {child && (
          <Card className="mb-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                  {child.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h2 className="font-fredoka text-xl font-bold">{child.name}</h2>
                  <p className="text-purple-100">Level {child.current_level || 1}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{(child.total_xp || 0).toLocaleString()}</div>
                  <p className="text-purple-100">Total XP</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Tabs */}
        <Tabs defaultValue="global" onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="global" className="gap-2" data-testid="tab-global">
              <Globe className="w-4 h-4" /> Global
            </TabsTrigger>
            <TabsTrigger value="weekly" className="gap-2" data-testid="tab-weekly">
              <Calendar className="w-4 h-4" /> This Week
            </TabsTrigger>
            {user?.organization_id && (
              <TabsTrigger value="organization" className="gap-2" data-testid="tab-org">
                <Building className="w-4 h-4" /> My Org
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="global">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-sky-500" />
                  Global Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <LeaderboardList data={globalLeaderboard} xpKey="xp" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="weekly">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-500" />
                  Weekly XP Champions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <LeaderboardList data={weeklyLeaderboard} xpKey="weekly_xp" />
              </CardContent>
            </Card>
          </TabsContent>

          {user?.organization_id && (
            <TabsContent value="organization">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5 text-purple-500" />
                    Organization Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <LeaderboardList data={orgLeaderboard} xpKey="xp" />
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default LeaderboardPage;
